import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Autocomplete,
    Chip,
    Tooltip,
    Alert,
    Card,
    CardContent,
    Stack,
    Divider,
    LinearProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    InputAdornment,
    useTheme,
    alpha
} from '@mui/material';
import {
    Science as ScienceIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Settings as SettingsIcon,
    Search as SearchIcon,
    ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';
import { testTypes, testMethods, testStandards, testStages } from './constants';

export default function ConfigurationStep({ 
    formData, 
    setFormData,
    onValidationChange,
    batchId,
    batchNumber,
    productInfo,
    showSnackbar,
    batchIngredients = []
}) {
    const theme = useTheme();
    const [validationErrors, setValidationErrors] = useState({});
    const [configurationComplete, setConfigurationComplete] = useState(false);
    const [testTypeDialogOpen, setTestTypeDialogOpen] = useState(false);
    const [testTypeSearchTerm, setTestTypeSearchTerm] = useState('');
    const [testCategoryDialogOpen, setTestCategoryDialogOpen] = useState(false);
    const [testCategorySearchTerm, setTestCategorySearchTerm] = useState('');
    const [testStageDialogOpen, setTestStageDialogOpen] = useState(false);
    const [testStageSearchTerm, setTestStageSearchTerm] = useState('');

    // Comprehensive validation logic
    const validateConfiguration = () => {
        const errors = {};
        
        if (!formData.test_category) {
            errors.test_category = 'Test category is required';
        }
        
        if (!formData.test_stage) {
            errors.test_stage = 'Test stage is required';
        }
        
        if (!formData.test_type) {
            errors.test_type = 'Test type is required';
        }
        
        if (!formData.test_method) {
            errors.test_method = 'Test method is required';
        }
        
        if (!formData.test_standard) {
            errors.test_standard = 'Test standard is required';
        }


        // Note: Removed compatibility checks to allow custom typed methods and standards
        // Users can now type their own methods and standards freely

        setValidationErrors(errors);
        const isValid = Object.keys(errors).length === 0;
        setConfigurationComplete(isValid);
        
        return isValid;
    };

    // Notify parent of validation status when data changes
    useEffect(() => {
        const timer = setTimeout(() => {
            const isValid = validateConfiguration();
            if (onValidationChange) {
                onValidationChange('configuration', isValid);
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [
        formData.test_category,
        formData.test_stage, 
        formData.test_type, 
        formData.test_method, 
        formData.test_standard,
    ]);

    // Initialize form data with batch/product context
    useEffect(() => {
        if (batchId && !formData.batch_id) {
            setFormData(prev => ({
                ...prev,
                batch_id: batchId,
                product_id: productInfo?.id || '',
                product_name: productInfo?.name || '',
                product_sku: productInfo?.sku || productInfo?.product_code || ''
            }));
        }
    }, [batchId, productInfo, formData.batch_id, setFormData]);



    const handleTestCategoryChange = (value) => {
        setFormData(prev => ({
            ...prev,
            test_category: value,
            // Reset dependent fields when category changes
            test_stage: prev.test_stage || (value === 'final_product' ? 'finished_goods' : 'mixing'),
            test_type: '',
            test_method: '',
            test_standard: '',
            target_unit: '',
        }));
        
        setTestCategoryDialogOpen(false);
        setTestCategorySearchTerm('');
        showSnackbar?.(`Test category set to ${value.replace('_', ' ')}`, 'info');
    };

    const handleTestStageChange = (value) => {
        setFormData(prev => ({
            ...prev,
            test_stage: value
        }));
        
        setTestStageDialogOpen(false);
        setTestStageSearchTerm('');
    };


    const handleTestTypeChange = (value) => {
        const testType = testTypes.find(t => t.value === value);
        setFormData(prev => ({
            ...prev,
            test_type: value,
            target_unit: testType?.unit || '',
            // Clear method and standard to force reselection
            test_method: '',
            test_standard: '',
            // Set some sensible defaults based on test type
            ...(testType?.category === 'Physical' && {
                qa_review_required: true
            }),
            ...(testType?.category === 'Microbiological' && {
                is_critical_test: true,
                qa_review_required: true
            })
        }));
        
        setTestTypeDialogOpen(false);
        setTestTypeSearchTerm('');
        showSnackbar?.(`Test type set to ${testType?.label || value}. Unit: ${testType?.unit}`, 'success');
    };

    // Filter test types based on search term
    const filteredTestTypes = testTypes.filter(type => 
        type.label.toLowerCase().includes(testTypeSearchTerm.toLowerCase()) ||
        type.category.toLowerCase().includes(testTypeSearchTerm.toLowerCase()) ||
        type.value.toLowerCase().includes(testTypeSearchTerm.toLowerCase()) ||
        type.description.toLowerCase().includes(testTypeSearchTerm.toLowerCase())
    );

    // Define test categories for dialog
    const testCategories = [
        {
            value: 'in_process',
            label: 'In-Process Production',
            description: 'Tests performed during mixing, blending, and production'
        },
        {
            value: 'bulk_product',
            label: 'Bulk Product',
            description: 'Tests on completed batch before transfer from production tank'
        },
        {
            value: 'stability',
            label: 'Stability',
            description: 'Long-term stability testing of production batches'
        }
    ];

    // Filter test categories based on search term
    const filteredTestCategories = testCategories.filter(category => 
        category.label.toLowerCase().includes(testCategorySearchTerm.toLowerCase()) ||
        category.value.toLowerCase().includes(testCategorySearchTerm.toLowerCase()) ||
        category.description.toLowerCase().includes(testCategorySearchTerm.toLowerCase())
    );

    // Filter test stages based on search term
    const filteredTestStages = testStages.filter(stage => 
        stage.label.toLowerCase().includes(testStageSearchTerm.toLowerCase()) ||
        stage.value.toLowerCase().includes(testStageSearchTerm.toLowerCase()) ||
        stage.description.toLowerCase().includes(testStageSearchTerm.toLowerCase())
    );


    const handleTestMethodChange = (value) => {
        const methodValue = typeof value === 'string' ? value : value?.value || '';
        setFormData(prev => ({
            ...prev,
            test_method: methodValue
        }));
        
        if (methodValue) {
            showSnackbar?.(`Test method selected: ${methodValue}`, 'info');
        }
    };

    const handleTestStandardChange = (value) => {
        const standardValue = typeof value === 'string' ? value : value?.value || '';
        setFormData(prev => ({
            ...prev,
            test_standard: standardValue
        }));
        
        if (standardValue) {
            showSnackbar?.(`Test standard selected: ${standardValue}`, 'info');
        }
    };

    const handleCriticalTestChange = (checked) => {
        setFormData(prev => ({
            ...prev,
            is_critical_test: checked,
            // Critical tests always require QA review
            qa_review_required: checked || prev.qa_review_required
        }));
        
        if (checked) {
            showSnackbar?.('Test marked as Critical Quality Attribute (CQA)', 'warning');
        }
    };

    // Get configuration completion percentage
    const getCompletionPercentage = () => {
        const requiredFields = ['test_category', 'test_stage', 'test_type', 'test_method', 'test_standard'];
        const completedFields = requiredFields.filter(field => formData[field]);
        return (completedFields.length / requiredFields.length) * 100;
    };

    const completionPercentage = getCompletionPercentage();



    return (
        <Box sx={{ p: 4 }}>
            {/* Header with Progress */}
            <Card sx={{ 
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mb: 4
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            backdropFilter: 'blur(10px)'
                        }}>
                            <ScienceIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                        </Box>
                        <Box flex={1}>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                                Test Configuration
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Define test parameters, methods, and standards for quality control
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h5" sx={{ fontWeight: 500, color: configurationComplete ? theme.palette.success.main : theme.palette.warning.main }}>
                                {Math.round(completionPercentage)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Complete
                            </Typography>
                        </Box>
                    </Stack>
                    
                    <LinearProgress 
                        variant="determinate" 
                        value={completionPercentage} 
                        sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.grey[400], 0.3),
                            '& .MuiLinearProgress-bar': {
                                background: configurationComplete ? 
                                    `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})` :
                                    `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                            }
                        }} 
                    />
                </CardContent>
            </Card>

            {/* Batch/Product Context */}
            {(batchId || productInfo) && (
                <Card sx={{ 
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    backdropFilter: 'blur(20px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    mb: 3
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                            Test Context
                        </Typography>
                        <Stack direction="row" spacing={3}>
                            {(batchNumber || batchId) && (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Batch Number: {batchNumber || batchId}
                                </Typography>
                            )}
                            {productInfo?.name && (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Product: {productInfo.name}
                                </Typography>
                            )}
                            {productInfo?.sku && (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    SKU: {productInfo.sku}
                                </Typography>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* Validation Alerts */}
            {Object.keys(validationErrors).length > 0 && (
                <Alert 
                    severity="error" 
                    sx={{ 
                        mb: 3,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        '& .MuiAlert-icon': {
                            color: theme.palette.error.main
                        }
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: theme.palette.error.main }}>
                        Configuration Issues Found:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {Object.values(validationErrors).map((error, index) => (
                            <li key={index}>
                                <Typography variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 500 }}>{error}</Typography>
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            {configurationComplete && (
                <Alert 
                    severity="success" 
                    sx={{ 
                        mb: 3,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        '& .MuiAlert-icon': {
                            color: theme.palette.success.main
                        }
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckCircleIcon />
                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            Configuration Complete - Ready for specifications
                        </Typography>
                    </Stack>
                </Alert>
            )}
            
            <Grid container spacing={4}>
                {/* Test Category */}
                <Grid item xs={12} md={6}>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                            Test Category *
                        </Typography>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => setTestCategoryDialogOpen(true)}
                            endIcon={<ArrowDropDownIcon />}
                            sx={{
                                justifyContent: 'space-between',
                                border: `1px solid ${validationErrors.test_category ? theme.palette.error.main : theme.palette.grey[400]}`,
                                borderRadius: 3,
                                py: 2,
                                px: 3,
                                backgroundColor: 'white',
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    border: `1px solid ${theme.palette.primary.main}`
                                },
                                textAlign: 'left',
                                color: formData.test_category ? theme.palette.text.primary : theme.palette.text.secondary
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formData.test_category ? 
                                    testCategories.find(c => c.value === formData.test_category)?.label || formData.test_category
                                    : 'Select Test Category'
                                }
                            </Typography>
                        </Button>
                        {validationErrors.test_category && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {validationErrors.test_category}
                            </Typography>
                        )}
                    </Box>
                </Grid>
                
                {/* Test Stage */}
                <Grid item xs={12} md={6}>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                            Test Stage *
                        </Typography>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => setTestStageDialogOpen(true)}
                            endIcon={<ArrowDropDownIcon />}
                            sx={{
                                justifyContent: 'space-between',
                                border: `1px solid ${validationErrors.test_stage ? theme.palette.error.main : theme.palette.grey[400]}`,
                                borderRadius: 3,
                                py: 2,
                                px: 3,
                                backgroundColor: 'white',
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    border: `1px solid ${theme.palette.primary.main}`
                                },
                                textAlign: 'left',
                                color: formData.test_stage ? theme.palette.text.primary : theme.palette.text.secondary
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formData.test_stage ? 
                                    testStages.find(s => s.value === formData.test_stage)?.label || formData.test_stage
                                    : 'Select Test Stage'
                                }
                            </Typography>
                        </Button>
                        {validationErrors.test_stage && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {validationErrors.test_stage}
                            </Typography>
                        )}
                    </Box>
                </Grid>

                
                {/* Test Type */}
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Test Type *"
                        value={formData.test_type || ''}
                        onChange={(e) => setFormData(prev => ({...prev, test_type: e.target.value}))}
                        error={!!validationErrors.test_type}
                        helperText={validationErrors.test_type || "Type your own test type or click dropdown to select from presets"}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => setTestTypeDialogOpen(true)}
                                        sx={{ 
                                            minWidth: 'auto', 
                                            px: 2,
                                            py: 1,
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                            color: 'white',
                                            fontWeight: 500,
                                            borderRadius: 2,
                                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                                            '&:hover': {
                                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                                transform: 'scale(1.05)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`
                                            }
                                        }}
                                    >
                                        <ArrowDropDownIcon />
                                        Presets
                                    </Button>
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    border: `1px solid ${theme.palette.grey[400]}`,
                                    borderRadius: 3
                                },
                                '&:hover fieldset': {
                                    border: `1px solid ${theme.palette.primary.main}`
                                },
                                '&.Mui-focused fieldset': {
                                    border: `1px solid ${theme.palette.primary.main}`
                                },
                                '& input': {
                                    py: 2,
                                    fontWeight: 500
                                },
                                backgroundColor: 'white',
                                borderRadius: 3,
                                overflow: 'hidden',
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                }
                            }
                        }}
                    />
                </Grid>
                
                {/* Critical Test Toggle */}
                <Grid item xs={12}>
                    <Card sx={{ 
                        borderRadius: 4,
                        border: formData.is_critical_test ? 
                            `1px solid ${theme.palette.error.main}` : 
                            `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <WarningIcon sx={{ 
                                    color: formData.is_critical_test ? theme.palette.error.main : theme.palette.grey[500],
                                    fontSize: '1.5rem'
                                }} />
                                <Box flex={1}>
                    <FormControlLabel
                        control={
                            <Switch
                                                checked={formData.is_critical_test || false}
                                                onChange={(e) => handleCriticalTestChange(e.target.checked)}
                                                color="error"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    Critical Quality Attribute (CQA)
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    Mark this test as critical for product quality and safety
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                
                {/* Test Method */}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        freeSolo
                        clearOnBlur={false}
                        selectOnFocus={true}
                        options={testMethods[formData.test_type] || []}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.value}
                        value={formData.test_method || ''}
                        onChange={(e, value) => handleTestMethodChange(value)}
                        onInputChange={(e, value) => setFormData(prev => ({...prev, test_method: value || ''}))}
                        disabled={!formData.test_type}
                        renderOption={(props, option) => (
                            <Tooltip title={option.tooltip || ''} placement="right" arrow>
                                <li {...props} style={{ padding: '12px 16px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {typeof option === 'string' ? option : option.value}
                                    </Typography>
                                </li>
                            </Tooltip>
                        )}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Test Method *" 
                                placeholder="Type your own method or select from suggestions"
                                required
                                error={!!validationErrors.test_method}
                                helperText={
                                    validationErrors.test_method ||
                                    (formData.test_type ? "You can type your own method or select from suggested options" : "Select test type first")
                                }
                                InputLabelProps={{
                                    ...params.InputLabelProps,
                                    sx: { fontWeight: 500 }
                                }}
                                sx={{ 
                                    cursor: 'text',
                                    '& .MuiOutlinedInput-root': { 
                                        cursor: 'text',
                                        '& fieldset': {
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            borderRadius: 3
                                        },
                                        '&:hover fieldset': {
                                            border: `1px solid ${theme.palette.primary.main}`
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: `1px solid ${theme.palette.primary.main}`
                                        },
                                        '& input': {
                                            py: 2,
                                            fontWeight: 500,
                                            cursor: 'text'
                                        },
                                        backgroundColor: 'white',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        backdropFilter: 'blur(10px)',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                        }
                                    }
                                }}
                            />
                        )}
                    />
                </Grid>
                
                {/* Test Standard */}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        freeSolo
                        clearOnBlur={false}
                        selectOnFocus={true}
                        options={testStandards[formData.test_type] || []}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.value}
                        value={formData.test_standard || ''}
                        onChange={(e, value) => handleTestStandardChange(value)}
                        onInputChange={(e, value) => setFormData(prev => ({...prev, test_standard: value || ''}))}
                        disabled={!formData.test_type}
                        renderOption={(props, option) => (
                            <Tooltip title={option.tooltip || ''} placement="right" arrow>
                                <li {...props} style={{ padding: '12px 16px' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {typeof option === 'string' ? option : option.value}
                                    </Typography>
                                </li>
                            </Tooltip>
                        )}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Test Standard *" 
                                placeholder="Type your own standard or select from suggestions"
                                required
                                error={!!validationErrors.test_standard}
                                helperText={
                                    validationErrors.test_standard ||
                                    (formData.test_type ? "You can type your own standard or select from suggested options" : "Select test type first")
                                }
                                InputLabelProps={{
                                    ...params.InputLabelProps,
                                    sx: { fontWeight: 500 }
                                }}
                                sx={{
                                    cursor: 'text',
                                    '& .MuiOutlinedInput-root': {
                                        cursor: 'text',
                                        '& fieldset': {
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            borderRadius: 3
                                        },
                                        '&:hover fieldset': {
                                            border: `1px solid ${theme.palette.primary.main}`
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: `1px solid ${theme.palette.primary.main}`
                                        },
                                        '& input': {
                                            py: 2,
                                            fontWeight: 500,
                                            cursor: 'text'
                                        },
                                        backgroundColor: 'white',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        backdropFilter: 'blur(10px)',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                        }
                                    }
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>

            {/* Test Type Selection Dialog */}
            <Dialog 
                open={testTypeDialogOpen} 
                onClose={() => setTestTypeDialogOpen(false)} 
                maxWidth="md" 
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        maxHeight: '80vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: '1.25rem',
                    pb: 2
                }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <ScienceIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Select Test Type
                        </Typography>
                    </Stack>
                </DialogTitle>
                
                <DialogContent sx={{ p: 0, overflowX: 'hidden' }}>
                    <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
                        <TextField
                            fullWidth
                            placeholder="Search test types, categories, or descriptions..."
                            value={testTypeSearchTerm}
                            onChange={(e) => setTestTypeSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        border: `1px solid ${theme.palette.grey[400]}`
                                    },
                                    '&:hover fieldset': {
                                        border: `1px solid ${theme.palette.primary.main}`
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: `1px solid ${theme.palette.primary.main}`
                                    }
                                }
                            }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
                            {filteredTestTypes.length} of {testTypes.length} test types shown
                        </Typography>
                    </Box>
                    
                    <List sx={{ maxHeight: '50vh', overflowY: 'auto', overflowX: 'hidden', p: 0 }}>
                        {filteredTestTypes.map((type, index) => (
                            <ListItemButton
                                key={type.value}
                                onClick={() => handleTestTypeChange(type.value)}
                                selected={formData.test_type === type.value}
                                sx={{
                                    py: 2,
                                    px: 3,
                                                                        borderBottom: index < filteredTestTypes.length - 1 ? `1px solid ${theme.palette.grey[200]}` : 'none',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                        transform: 'translateX(4px)',
                                        borderLeft: `4px solid #000000`,
                                        transition: 'all 0.2s ease'
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.16),
                                            borderLeft: `4px solid #000000`
                                        }
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box>
                                                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                                                    {type.label}
                                                </Typography>
                                                <Chip 
                                                    label={type.category} 
                                                    size="small" 
                                                    sx={{ 
                                                        fontSize: '0.75rem', 
                                                        height: 22,
                                                        fontWeight: 500,
                                                        backgroundColor: 
                                                            type.category === 'Chemical' ? alpha(theme.palette.info.main, 0.1) :
                                                            type.category === 'Physical' ? alpha(theme.palette.primary.main, 0.1) :
                                                            type.category === 'Microbiological' ? alpha(theme.palette.error.main, 0.1) :
                                                            type.category === 'Sensory' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                                                        color: 
                                                            type.category === 'Chemical' ? theme.palette.info.main :
                                                            type.category === 'Physical' ? theme.palette.primary.main :
                                                            type.category === 'Microbiological' ? theme.palette.error.main :
                                                            type.category === 'Sensory' ? theme.palette.success.main : theme.palette.grey[600]
                                                    }} 
                                                />
                                                <Chip 
                                                    label={type.unit} 
                                                    size="small" 
                                                    variant="outlined"
                                                    sx={{ 
                                                        fontSize: '0.75rem', 
                                                        height: 22,
                                                        fontWeight: 500,
                                                        borderColor: theme.palette.primary.main,
                                                        color: theme.palette.primary.main
                                                    }} 
                                                />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                                {type.description}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItemButton>
                        ))}
                        {filteredTestTypes.length === 0 && (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    No test types found matching "{testTypeSearchTerm}"
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Try adjusting your search terms
                                </Typography>
                            </Box>
                        )}
                    </List>
                </DialogContent>
                
                <DialogActions sx={{ 
                    p: 3, 
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.01)})`
                }}>
                    <Button 
                        onClick={() => {
                            setTestTypeDialogOpen(false);
                            setTestTypeSearchTerm('');
                        }}
                        sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.grey[400], 0.1),
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test Category Selection Dialog */}
            <Dialog 
                open={testCategoryDialogOpen} 
                onClose={() => setTestCategoryDialogOpen(false)} 
                maxWidth="md" 
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        maxHeight: '80vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: '1.25rem',
                    pb: 2
                }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <SettingsIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Select Test Category
                        </Typography>
                    </Stack>
                </DialogTitle>
                
                <DialogContent sx={{ p: 0, overflowX: 'hidden' }}>
                    <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
                        <TextField
                            fullWidth
                            placeholder="Search test categories or descriptions..."
                            value={testCategorySearchTerm}
                            onChange={(e) => setTestCategorySearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        border: `1px solid ${theme.palette.grey[400]}`
                                    },
                                    '&:hover fieldset': {
                                        border: `1px solid ${theme.palette.primary.main}`
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: `1px solid ${theme.palette.primary.main}`
                                    }
                                }
                            }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
                            {filteredTestCategories.length} of {testCategories.length} categories shown
                        </Typography>
                    </Box>
                    
                    <List sx={{ maxHeight: '50vh', overflowY: 'auto', overflowX: 'hidden', p: 0 }}>
                        {filteredTestCategories.map((category, index) => (
                            <ListItemButton
                                key={category.value}
                                onClick={() => handleTestCategoryChange(category.value)}
                                selected={formData.test_category === category.value}
                                sx={{
                                    py: 2,
                                    px: 3,
                                    borderBottom: index < filteredTestCategories.length - 1 ? `1px solid ${theme.palette.grey[200]}` : 'none',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                        transform: 'translateX(4px)',
                                        borderLeft: `4px solid #000000`,
                                        transition: 'all 0.2s ease'
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.16),
                                            borderLeft: `4px solid #000000`
                                        }
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 1 }}>
                                                {category.label}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                                {category.description}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItemButton>
                        ))}
                        {filteredTestCategories.length === 0 && (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    No categories found matching "{testCategorySearchTerm}"
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Try adjusting your search terms
                                </Typography>
                            </Box>
                        )}
                    </List>
                </DialogContent>
                
                <DialogActions sx={{ 
                    p: 3, 
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.01)})`
                }}>
                    <Button 
                        onClick={() => {
                            setTestCategoryDialogOpen(false);
                            setTestCategorySearchTerm('');
                        }}
                        sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.grey[400], 0.1),
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test Stage Selection Dialog */}
            <Dialog 
                open={testStageDialogOpen} 
                onClose={() => setTestStageDialogOpen(false)} 
                maxWidth="md" 
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        maxHeight: '80vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: '1.25rem',
                    pb: 2
                }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <SettingsIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Select Test Stage
                        </Typography>
                    </Stack>
                </DialogTitle>
                
                <DialogContent sx={{ p: 0, overflowX: 'hidden' }}>
                    <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
                        <TextField
                            fullWidth
                            placeholder="Search test stages or descriptions..."
                            value={testStageSearchTerm}
                            onChange={(e) => setTestStageSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        border: `1px solid ${theme.palette.grey[400]}`
                                    },
                                    '&:hover fieldset': {
                                        border: `1px solid ${theme.palette.primary.main}`
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: `1px solid ${theme.palette.primary.main}`
                                    }
                                }
                            }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
                            {filteredTestStages.length} of {testStages.length} stages shown
                        </Typography>
                    </Box>
                    
                    <List sx={{ maxHeight: '50vh', overflowY: 'auto', overflowX: 'hidden', p: 0 }}>
                        {filteredTestStages.map((stage, index) => (
                            <ListItemButton
                                key={stage.value}
                                onClick={() => handleTestStageChange(stage.value)}
                                selected={formData.test_stage === stage.value}
                                sx={{
                                    py: 2,
                                    px: 3,
                                    borderBottom: index < filteredTestStages.length - 1 ? `1px solid ${theme.palette.grey[200]}` : 'none',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                        transform: 'translateX(4px)',
                                        borderLeft: `4px solid #000000`,
                                        transition: 'all 0.2s ease'
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.16),
                                            borderLeft: `4px solid #000000`
                                        }
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 1 }}>
                                                {stage.label}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                                {stage.description}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItemButton>
                        ))}
                        {filteredTestStages.length === 0 && (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    No stages found matching "{testStageSearchTerm}"
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Try adjusting your search terms
                                </Typography>
                            </Box>
                        )}
                    </List>
                </DialogContent>
                
                <DialogActions sx={{ 
                    p: 3, 
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.01)})`
                }}>
                    <Button 
                        onClick={() => {
                            setTestStageDialogOpen(false);
                            setTestStageSearchTerm('');
                        }}
                        sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.grey[400], 0.1),
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
} 